<template>
  <aside class="wfp" :class="{ 'is-collapsed': collapsed }">
    <div class="wfp-tabs">
      <template v-if="!collapsed">
        <span class="wfp-tab is-on">Data</span>
      </template>
      <button class="wfp-collapse" :title="collapsed ? 'Show data panel' : 'Hide data panel'" @click="collapsed = !collapsed">
        <font-awesome-icon :icon="['fas', collapsed ? 'chevron-right' : 'chevron-left']" />
      </button>
    </div>

    <template v-if="!collapsed">
      <div class="wfp-search">
        <input v-model.trim="query" type="text" placeholder="Search sources and fields" />
      </div>
      <div class="wfp-scroll">
        <p v-if="loading" class="wfp-muted">Loading sources…</p>
        <template v-for="group in groups" :key="group.label">
          <div v-if="group.items.length" class="wfp-group">{{ group.label }}<span class="wfp-group-count">{{ group.items.length }}</span></div>
          <div v-for="da in group.items" :key="da.id" class="wfp-folder">
            <div
              class="wfp-folder-head"
              :class="{ 'is-unavailable': runtimeParametersNote(da) }"
              :draggable="!runtimeParametersNote(da)"
              :title="runtimeParametersNote(da) || (onCanvas.has(da.id) ? da.name : 'Drag onto the canvas, or double-click to add')"
              @dragstart="dragSource($event, da)"
              @click="toggle(da)"
              @dblclick="!onCanvas.has(da.id) && !runtimeParametersNote(da) && $emit('add-source', da)"
            >
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="wfp-chev" :class="{ 'is-open': open[da.id] }" />
              <font-awesome-icon :icon="['fas', 'table']" class="wfp-folder-icon" />
              <span class="wfp-folder-name" :title="da.name">{{ da.name }}</span>
              <span v-if="columnCount(da)" class="wfp-count" :title="`${columnCount(da)} columns`">{{ columnCount(da) }}</span>
              <span v-if="runtimeParametersNote(da)" class="wfp-param-tag">params</span>
              <button v-else-if="!onCanvas.has(da.id)" class="wfp-add" title="Add to canvas" @click.stop="$emit('add-source', da)">+</button>
            </div>
            <div v-if="open[da.id] || query" class="wfp-fields">
              <p v-if="types[da.id] && types[da.id].error" class="wfp-muted wfp-err">{{ types[da.id].error }}</p>
              <div
                v-for="f in fieldsOf(da)"
                :key="f.name"
                class="wfp-field"
                draggable="true"
                :title="sheetActive ? 'Click to add to the sheet, or drag onto a shelf' : 'Click to see its profile; drag onto a sheet shelf'"
                @dragstart="dragField($event, f)"
                @click="$emit('field-click', f)"
                @contextmenu.prevent="openMenu($event, f)"
              >
                <span class="wfp-glyph" :class="`is-${f.type || 'unknown'}`">{{ glyph(f.type) }}</span>
                <span class="wfp-field-name" :title="f.name">{{ f.name }}</span>
                <button class="wfp-more" title="Field actions" @click.stop="openMenu($event, f)">⋯</button>
              </div>
            </div>
          </div>
        </template>

        <div v-if="calculated.length" class="wfp-group">Calculated<span class="wfp-group-count">{{ calculated.length }}</span></div>
        <div
          v-for="c in calculated"
          :key="`calc-${c.name}`"
          class="wfp-field wfp-field--calc"
          draggable="true"
          title="Click to edit; drag onto a sheet shelf"
          @dragstart="dragField($event, { name: c.name, type: 'calc' })"
          @click="$emit('edit-calc', c.name)"
        >
          <span class="wfp-glyph is-calc">ƒ</span>
          <span class="wfp-field-name" :title="c.name">{{ c.name }}</span>
          <span class="wfp-muted">{{ c.fn }}</span>
        </div>
      </div>
    </template>

    <div v-if="menu" class="wfp-menu" :style="{ top: `${menu.y}px`, left: `${menu.x}px` }" @click.stop>
      <button @click="act('calc')">Calculate from {{ menu.field.name }}…</button>
      <button @click="act('filter')">Filter on {{ menu.field.name }}…</button>
      <button @click="act('profile')">Show its profile</button>
    </div>
  </aside>
</template>

<script>
import axios from 'axios';
import { canvasContext } from '@/services/canvasContext';

import { runtimeParametersNote } from '@/utils/daRuntimeParams';

const ATTR_BASE = '/api/impact_analytics/attributes/';
const TYPES_URL = '/api/etl_module/data-workflows/source-columns/';
const GLYPHS = { text: 'abc', number: '#', date: 'dt', boolean: 'bool', other: '·', calc: 'ƒx' };

export default {
  name: 'WorkflowFieldsPanel',
  props: {
    sheetActive: { type: Boolean, default: false },
  },
  emits: ['add-source', 'field-click', 'field-action', 'edit-calc'],
  data() {
    return {
      collapsed: false, query: '', loading: false,
      sources: [], types: {}, open: {}, menu: null, canvas: canvasContext,
    };
  },
  computed: {
    onCanvas() {
      return new Set((this.canvas.nodes || []).map((n) => n.da_id));
    },
    calculated() {
      return this.canvas.calculated || [];
    },
    groups() {
      const q = this.query.toLowerCase();
      const match = (da) => !q || da.name.toLowerCase().includes(q)
        || this.fieldsOf(da).some((f) => f.name.toLowerCase().includes(q));
      const visible = this.sources.filter(match);
      return [
        { label: 'On the canvas', items: visible.filter((d) => this.onCanvas.has(d.id)) },
        { label: 'Available', items: visible.filter((d) => !this.onCanvas.has(d.id)) },
      ];
    },
  },
  watch: {
    onCanvas(ids, before) {
      const added = [...ids].filter((id) => !before || !before.has(id));
      if (added.length) this.open = { ...this.open, ...Object.fromEntries(added.map((id) => [id, true])) };
      this.loadTypes([...ids]);
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeMenu);
    this.loading = true;
    try {
      const { data } = await axios.get(ATTR_BASE);
      this.sources = (Array.isArray(data) ? data : (data.results || [])).slice().sort((a, b) => a.name.localeCompare(b.name));
    } finally {
      this.loading = false;
    }
    this.open = { ...this.open, ...Object.fromEntries([...this.onCanvas].map((id) => [id, true])) };
    this.loadTypes([...this.onCanvas]);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeMenu);
  },
  methods: {
    runtimeParametersNote,
    fieldsOf(da) {
      const typed = this.types[da.id] && this.types[da.id].columns;
      const fields = typed || (da.output_columns || []).map((name) => ({ name, type: null }));
      const q = this.query.toLowerCase();
      return q && !da.name.toLowerCase().includes(q) ? fields.filter((f) => f.name.toLowerCase().includes(q)) : fields;
    },
    async loadTypes(ids) {
      const missing = ids.filter((id) => id && !this.types[id]);
      if (!missing.length) return;
      missing.forEach((id) => { this.types = { ...this.types, [id]: { pending: true } }; });
      const { data } = await axios.post(TYPES_URL, { da_ids: missing });
      const next = { ...this.types };
      missing.forEach((id) => { next[id] = (data.sources || {})[String(id)] || { error: 'Could not read columns.' }; });
      this.types = next;
      const published = { ...this.canvas.columnTypes };
      missing.forEach((id) => {
        const cols = next[id] && next[id].columns;
        if (cols) published[id] = Object.fromEntries(cols.map((c) => [c.name, c.type]));
      });
      this.canvas.columnTypes = published;
    },
    toggle(da) {
      this.open = { ...this.open, [da.id]: !this.open[da.id] };
      if (this.open[da.id]) this.loadTypes([da.id]);
    },
    columnCount(da) {
      const typed = this.types[da.id] && this.types[da.id].columns;
      return typed ? typed.length : (da.output_columns || []).length;
    },
    glyph(type) {
      return GLYPHS[type] || '—';
    },
    dragSource(ev, da) {
      ev.dataTransfer.setData('da', JSON.stringify({ id: da.id, name: da.name }));
      ev.dataTransfer.effectAllowed = 'copyMove';
    },
    dragField(ev, f) {
      ev.dataTransfer.setData('field', JSON.stringify({ name: f.name, type: f.type }));
      ev.dataTransfer.effectAllowed = 'copy';
    },
    openMenu(ev, field) {
      this.menu = { field, x: ev.clientX, y: ev.clientY };
    },
    closeMenu() {
      this.menu = null;
    },
    act(action) {
      this.$emit('field-action', { action, field: this.menu.field });
      this.menu = null;
    },
  },
};
</script>

<style scoped>
.wfp { width: 232px; flex-shrink: 0; display: flex; flex-direction: column; min-height: 0; border-right: 1px solid #E8E9E9; background: #FAFAFA; font-size: 12px; }
.wfp.is-collapsed { width: 32px; }
.wfp-tabs { display: flex; align-items: stretch; height: 32px; border-bottom: 1px solid #E8E9E9; }
.wfp-tab { flex: 1; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: #626264; border-bottom: 2px solid transparent; }
.wfp-tab.is-on { color: #1E1F21; border-bottom-color: #F76734; }
.wfp-collapse:focus-visible { outline: 2px solid #4573D2; outline-offset: -2px; }
.wfp-collapse { width: 32px; border: 0; background: transparent; color: #626264; cursor: pointer; font-size: 10px; }
.wfp-search { padding: 4px 8px; border-bottom: 1px solid #E8E9E9; }
.wfp-search input { width: 100%; box-sizing: border-box; height: 24px; border: 1px solid #E8E9E9; border-radius: 4px; padding: 0 8px; font: 12px ui-monospace, SFMono-Regular, Menlo, monospace; color: #353537; background: #FFFFFF; }
.wfp-search input:focus { outline: 2px solid #4573D2; outline-offset: -1px; }
.wfp-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 4px 0 8px; }
.wfp-group { display: flex; align-items: center; justify-content: space-between; height: 24px; padding: 0 8px 0 12px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #626264; }
.wfp-group-count { font: 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #8E8F90; letter-spacing: 0; }
.wfp-folder-head { display: flex; align-items: center; gap: 4px; height: 22px; padding: 0 8px 0 4px; cursor: pointer; }
.wfp-folder-head:hover { background: #F4F4F4; }
.wfp-chev { width: 12px; flex-shrink: 0; font-size: 8px; color: #626264; transition: transform .1s ease; }
.wfp-chev.is-open { transform: rotate(90deg); }
.wfp-folder-icon { width: 12px; flex-shrink: 0; font-size: 10px; color: #CC5F38; }
.wfp-folder-name { flex: 1; min-width: 0; font: 600 12px/22px ui-monospace, SFMono-Regular, Menlo, monospace; color: #1E1F21; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wfp-count { flex-shrink: 0; font: 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #8E8F90; }
.wfp-add { flex-shrink: 0; width: 16px; height: 16px; border: 0; border-radius: 4px; background: transparent; color: #4C6E97; font-size: 14px; line-height: 1; cursor: pointer; opacity: 0; }
.wfp-folder-head:hover .wfp-add, .wfp-add:focus-visible { opacity: 1; }
.wfp-add:hover { background: #EBF4FF; }
.wfp-fields { margin-left: 10px; border-left: 1px solid #E8E9E9; }
.wfp-field { display: flex; align-items: center; gap: 8px; height: 22px; padding: 0 8px 0 12px; cursor: grab; }
.wfp-field:hover { background: #F4F4F4; }
.wfp-field--calc { padding-left: 16px; cursor: pointer; }
.wfp-glyph { flex-shrink: 0; width: 26px; font: 10px ui-monospace, SFMono-Regular, Menlo, monospace; text-align: right; color: #8E8F90; }
.wfp-glyph.is-number { color: #4C6E97; font-weight: 700; }
.wfp-glyph.is-date { color: #626264; }
.wfp-glyph.is-calc { color: #687C65; font-weight: 700; }
.wfp-field-name { flex: 1; min-width: 0; font: 12px/22px ui-monospace, SFMono-Regular, Menlo, monospace; color: #353537; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wfp-more { flex-shrink: 0; border: 0; background: transparent; color: #626264; cursor: pointer; opacity: 0; padding: 0 2px; }
.wfp-field:hover .wfp-more, .wfp-more:focus-visible { opacity: 1; }
.wfp-muted { font-size: 11px; color: #626264; }
.wfp-muted.wfp-err { color: #D53D3D; padding: 2px 12px; }
.wfp-folder-head.is-unavailable { opacity: .55; }
.wfp-param-tag { margin-left: auto; font-size: 10px; color: #626264; border: 1px solid #DDDDDD; border-radius: 4px; padding: 0 4px; }
.wfp-menu { position: fixed; z-index: 70; min-width: 200px; background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 4px; box-shadow: 0 0 40px rgba(0,0,0,.10); padding: 4px 0; display: flex; flex-direction: column; }
.wfp-menu button { text-align: left; border: 0; background: transparent; padding: 4px 12px; font: 12px ui-monospace, SFMono-Regular, Menlo, monospace; color: #353537; cursor: pointer; }
.wfp-menu button:hover { background: #F4F4F4; }
</style>
